import { useEffect, useMemo, useRef, useState } from "react";
import { UseFormSetValue, UseFormTrigger, FieldValues } from "react-hook-form";
import Over15Alert from "./Over15Alert";
import PassportNot5504Alert from "./PassportNot5504Alert";
import PassportOver1Alert from "./PassportOver1Alert";
import PassportOver2Alert from "./PassportOver2Alert";
import Under16Alert from "./Under16Alert";
import { getAge } from "@/utils/dates";

interface FieldsToWatch {
  birthDate: string;
  passportIssuanceDate: string;
  passportIssuanceDateOptional: string;
  cardIssuanceDate: string;
  cardIssuanceDateOptional: string;
  nameChange: "yes" | "no";
  incorrectPrintedInfo: "yes" | "no";
  limitedValidityPassport: "yes" | "no";
  documentType2: string;
}

interface FormAlertsPopupsProps {
  fieldsToWatch: FieldsToWatch;
  form: string;
  touchedFields: Record<string, boolean>;
  setValue: UseFormSetValue<FieldValues>;
}

export function getChangedFieldName(
  prevValues: Record<string, any>,
  newValues: Record<string, any>
): string | undefined {
  for (const key in newValues) {
    if (prevValues[key] !== newValues[key]) {
      return key;
    }
  }
  return undefined;
}

const enabledForm = (current: string, expected: string) => current === expected;

export const getAlertConditions = ({
  fieldsToWatch,
  form,
}: FormAlertsPopupsProps): Record<string, boolean> => {
  const {
    birthDate,
    passportIssuanceDate,
    passportIssuanceDateOptional,
    cardIssuanceDate,
    cardIssuanceDateOptional,
    nameChange,
    incorrectPrintedInfo,
    limitedValidityPassport,
    documentType2,
  } = fieldsToWatch;

  const age = getAge(birthDate);

  const passportIssuanceDateOrOptional = passportIssuanceDate || passportIssuanceDateOptional;
  const passportAge =
    passportIssuanceDateOrOptional !== "" ? getAge(passportIssuanceDateOrOptional) : null;
  const cardIssuanceDateOrOptional = cardIssuanceDate || cardIssuanceDateOptional;
  const cardAge = cardIssuanceDateOrOptional !== "" ? getAge(cardIssuanceDateOrOptional) : null;

  const userAgeUnder16_DS2 = age !== null && age < 16 && enabledForm(form, "DS-82");
  const passportAgeIsOver15_DS82 =
    passportAge !== null && passportAge !== 0 && passportAge > 14 && enabledForm(form, "DS-82");

  const cardAgeIsOver15_DS82 =
    cardAge !== null && cardAge !== 0 && cardAge! > 14 && enabledForm(form, "DS-82");

  const isForm_DS5504 = enabledForm(form, "DS-5504");
  const isDocumentTypePassportBook = documentType2 === "passportbook" || documentType2 === "both";
  const isDocumentTypePassportCard = documentType2 === "passportcard" || documentType2 === "both";

  const hasNameChange = nameChange === "yes";

  const isPassportAgeOver1Year = passportAge !== null && passportAge >= 1;
  const isCardAgeOver1Year = cardAge !== null && cardAge >= 1;

  const isPassportAgeOver2Years = passportAge !== null && passportAge > 2;
  const isCardAgeOver2Years = cardAge !== null && cardAge > 2;

  return {
    under16: userAgeUnder16_DS2,

    over15passport: passportAgeIsOver15_DS82,
    over15card: cardAgeIsOver15_DS82,

    passportBookOver1:
      isForm_DS5504 && isDocumentTypePassportBook && hasNameChange && isPassportAgeOver1Year,

    passportBookOver2:
      isForm_DS5504 &&
      isDocumentTypePassportBook &&
      limitedValidityPassport === "yes" &&
      isPassportAgeOver2Years,

    passportCardOver1:
      isForm_DS5504 && isDocumentTypePassportCard && hasNameChange && isCardAgeOver1Year,

    passportCardOver2:
      isForm_DS5504 &&
      isDocumentTypePassportCard &&
      limitedValidityPassport === "yes" &&
      isCardAgeOver2Years,

    not5504:
      isForm_DS5504 &&
      nameChange === "no" &&
      incorrectPrintedInfo === "no" &&
      limitedValidityPassport === "no",
  };
};

const FormAlertsPopups = (props: FormAlertsPopupsProps) => {
  const { fieldsToWatch, setValue, form } = props;
  const [changedField, setChangedField] = useState<string>();
  const [fieldToClear, setFieldToClear] = useState<string>();
  const prevFieldsRef = useRef<FieldsToWatch>(fieldsToWatch);
  const initialAlertCheckedRef = useRef(false);

  const alertToFieldMap: Record<keyof typeof alertComponents, string> = {
    under16: "birthDate",
    over15passport: "passportIssuanceDate",
    over15card: "cardIssuanceDate",
    passportBookOver1: "passportIssuanceDate",
    passportBookOver2: "passportIssuanceDate",
    passportCardOver1: "cardIssuanceDate",
    passportCardOver2: "cardIssuanceDate",
    not5504: "limitedValidityPassport",
  };

  useEffect(() => {
    initialAlertCheckedRef.current = false;
  }, [form]);

  // Detect changed field
  useEffect(() => {
    const prevFields = prevFieldsRef.current;
    const changed = getChangedFieldName(prevFields, fieldsToWatch);
    if (changed) setChangedField(changed);
    prevFieldsRef.current = { ...fieldsToWatch }; // <-- store a copy, not a reference
  }, [fieldsToWatch]);

  const [alerts, setAlerts] = useState({
    under16: false,
    over15passport: false,
    over15card: false,
    passportBookOver1: false,
    passportBookOver2: false,
    passportCardOver1: false,
    passportCardOver2: false,
    not5504: false,
  });

  const conditions = useMemo(() => getAlertConditions(props), [props]);

  useEffect(() => {
    setAlerts((prev) => {
      if (changedField) return prev;
      if (initialAlertCheckedRef.current) return prev;

      const priority = Object.keys(alertComponents) as Array<keyof typeof alertComponents>;
      const updated = { ...prev };
      let alertSet = false;

      for (const key of priority) {
        if (conditions[key]) {
          updated[key] = true;
          setFieldToClear(alertToFieldMap[key]);
          alertSet = true;
          break;
        }
      }

      if (alertSet) initialAlertCheckedRef.current = true;
      return alertSet ? updated : prev;
    });
  }, [conditions, changedField]);

  useEffect(() => {
    setAlerts((prev) => {
      const updated = { ...prev };
      let alertSet = false;
      switch (changedField) {
        case "birthDate":
          if (conditions.under16) {
            updated.under16 = true;
            setFieldToClear(alertToFieldMap.under16);
            alertSet = true;
          }
          break;
        case "passportIssuanceDate":
        case "passportIssuanceDateOptional":
          if (conditions.over15passport && !alertSet) {
            updated.over15passport = true;
            setFieldToClear(alertToFieldMap.over15passport);
            alertSet = true;
          }
          if (conditions.passportBookOver1 && !alertSet) {
            updated.passportBookOver1 = true;
            setFieldToClear(alertToFieldMap.passportBookOver1);
            alertSet = true;
          }
          if (conditions.passportBookOver2 && !alertSet) {
            updated.passportBookOver2 = true;
            setFieldToClear(alertToFieldMap.passportBookOver2);
            alertSet = true;
          }
          break;
        case "cardIssuanceDate":
        case "cardIssuanceDateOptional":
          if (conditions.over15card && !alertSet) {
            updated.over15card = true;
            setFieldToClear(alertToFieldMap.over15card);
            alertSet = true;
          }
          if (conditions.passportCardOver1 && !alertSet) {
            updated.passportCardOver1 = true;
            setFieldToClear(alertToFieldMap.passportCardOver1);
            alertSet = true;
          }
          if (conditions.passportCardOver2 && !alertSet) {
            updated.passportCardOver2 = true;
            setFieldToClear(alertToFieldMap.passportCardOver2);
            alertSet = true;
          }
          break;
        case "nameChange":
        case "incorrectPrintedInfo":
        case "limitedValidityPassport":
        case "documentType2":
          if (conditions.passportBookOver1 && !alertSet) {
            updated.passportBookOver1 = true;
            setFieldToClear(alertToFieldMap.passportBookOver1);
            alertSet = true;
          }
          if (conditions.passportBookOver2 && !alertSet) {
            updated.passportBookOver2 = true;
            setFieldToClear(alertToFieldMap.passportBookOver2);
            alertSet = true;
          }
          if (conditions.passportCardOver1 && !alertSet) {
            updated.passportCardOver1 = true;
            setFieldToClear(alertToFieldMap.passportCardOver1);
            alertSet = true;
          }
          if (conditions.passportCardOver2 && !alertSet) {
            updated.passportCardOver2 = true;
            setFieldToClear(alertToFieldMap.passportCardOver2);
            alertSet = true;
          }
          if (conditions.not5504 && !alertSet) {
            updated.not5504 = true;
            setFieldToClear(alertToFieldMap.not5504);
            alertSet = true;
          }
          break;
      }
      return updated;
    });
  }, [changedField, conditions]);

  const alertComponents = {
    under16: Under16Alert,
    over15passport: Over15Alert,
    over15card: Over15Alert,
    passportBookOver1: PassportOver1Alert,
    passportCardOver1: PassportOver1Alert,
    passportBookOver2: PassportOver2Alert,
    passportCardOver2: PassportOver2Alert,
    not5504: PassportNot5504Alert,
  };

  return (
    <>
      {Object.entries(alertComponents).map(([key, Component]) => {
        const alertKey = key as keyof typeof alerts;
        return alerts[alertKey] ? (
          <Component
            key={key}
            onClose={() => {
              setAlerts((prev) => ({ ...prev, [key]: false }));
              if (fieldToClear) setValue(fieldToClear, "");
              setFieldToClear(undefined);
              setChangedField(undefined);
            }}
          />
        ) : null;
      })}
    </>
  );
};

export default FormAlertsPopups;
